import { useState, useRef } from "react";
import api, { errMsg, limitInfo } from "@/lib/api";
import { Eyebrow } from "@/components/Eyebrow";
import { InfoTip } from "@/components/InfoTip";
import { EmptyState } from "@/components/EmptyState";
import { ShareKitDialog } from "@/components/ShareKitDialog";
import { toast } from "@/components/ui/sonner";
import { Upload, Link as LinkIcon, Plus, X, Globe, Share2, ScanSearch } from "lucide-react";

const PHASES = ["CONNECTING...", "SCRAPING HOMEPAGE...", "ANALYZING VOICE...", "EXTRACTING PALETTE...", "COMPLETE"];
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function ScanOverlay({ phase, flashing }) {
  return (
    <div className="scan-wrap" data-testid="scan-overlay">
      <div className={`scan-line ${flashing ? "flash" : ""}`} />
      <div className="scan-readout" data-testid="scan-readout">{PHASES[phase] || PHASES[0]}</div>
    </div>
  );
}

function SourceUrl({ url }) {
  if (!url) return null;
  return (
    <div className="reveal" style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 18 }}>
      <LinkIcon size={12} style={{ color: "var(--orange)", flexShrink: 0 }} />
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        data-testid="source-url"
        className="font-mono"
        style={{ fontSize: 11, color: "var(--orange)", textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        {url}
      </a>
    </div>
  );
}

function PaletteSection({ colors, onChange }) {
  const [picker, setPicker] = useState("#f97316");
  const add = () => {
    const hx = (picker || "").toLowerCase();
    if (!/^#[0-9a-f]{6}$/.test(hx) || colors.includes(hx)) return;
    onChange([...colors, hx]);
  };
  const remove = (c) => onChange(colors.filter((x) => x !== c));
  return (
    <div className="reveal" style={{ animationDelay: "0s" }}>
      <Eyebrow testId="eyebrow-palette">Palette</Eyebrow>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 14 }} data-testid="palette-swatches">
        {colors.length === 0 && (
          <div className="font-mono" style={{ fontSize: 12, color: "var(--tmuted)" }} data-testid="palette-empty">
            No palette detected — add brand colors below
          </div>
        )}
        {colors.map((c, i) => (
          <div key={`${c}-${i}`} style={{ textAlign: "center", position: "relative" }}>
            <div className="swatch" style={{ background: c }} title={c} data-testid={`swatch-${i}`} />
            <button
              data-testid={`remove-swatch-${i}`}
              onClick={() => remove(c)}
              aria-label={`Remove ${c}`}
              style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: 999, background: "var(--panel)", border: "1px solid var(--cardborder)", color: "var(--tsecondary)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}
            >
              <X size={11} />
            </button>
            <div className="font-mono" style={{ fontSize: 11, color: "var(--tsecondary)", marginTop: 5 }}>{c}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14 }}>
        <input
          type="color"
          data-testid="color-picker"
          value={picker}
          onChange={(e) => setPicker(e.target.value)}
          style={{ width: 34, height: 34, border: "1px solid var(--cardborder)", borderRadius: 6, background: "transparent", cursor: "pointer", padding: 2 }}
        />
        <button data-testid="add-color-button" className="btn-secondary" onClick={add} style={{ padding: "7px 12px", fontSize: 12 }}>
          <Plus size={13} /> Add Color
        </button>
      </div>
    </div>
  );
}

function TypefacesSection({ fonts }) {
  return (
    <div className="reveal" style={{ animationDelay: "0.2s", marginTop: 28 }}>
      <Eyebrow testId="eyebrow-typefaces">Typefaces</Eyebrow>
      <div style={{ marginTop: 12 }} data-testid="typefaces-list">
        {fonts.length === 0 && (
          <div className="font-mono" style={{ fontSize: 12, color: "var(--tmuted)" }}>No typefaces detected</div>
        )}
        {fonts.map((f, i) => (
          <div key={`${f}-${i}`} style={{ marginBottom: 10 }}>
            <div style={{ fontFamily: `'${f}', Inter, sans-serif`, fontSize: 20, color: "var(--tprimary)", lineHeight: 1.2 }}>{f}</div>
            <div className="font-mono" style={{ fontSize: 10, color: "var(--tmuted)" }}>Aa Bb Cc 123</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function VoiceSection({ value, onChange, onBlur }) {
  return (
    <div className="reveal" style={{ animationDelay: "0.4s", marginTop: 28 }}>
      <Eyebrow testId="eyebrow-voice">Voice</Eyebrow>
      <textarea
        data-testid="voice-textarea"
        className="dim-input font-body"
        style={{ marginTop: 12, minHeight: 96 }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
      />
    </div>
  );
}

function AssetsSection({ images, fileRef, onUpload }) {
  return (
    <div className="reveal" style={{ animationDelay: "0.6s", marginTop: 28 }}>
      <Eyebrow testId="eyebrow-assets">Assets</Eyebrow>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 12 }} data-testid="assets-grid">
        {images.slice(0, 8).map((src, i) => (
          <div key={`${src.slice(0, 24)}-${i}`} className="panel-card" style={{ aspectRatio: "1/1", overflow: "hidden", padding: 0 }}>
            <img src={src} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
          </div>
        ))}
      </div>
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={onUpload} />
      <button data-testid="upload-asset-button" className="btn-secondary" style={{ width: "100%", marginTop: 12 }} onClick={() => fileRef.current?.click()}>
        <Upload size={14} /> Upload
      </button>
    </div>
  );
}

export function BrandDNAPanel({ brandDna, setBrandDna, onAnalyzed, brandId, pulsing, onLimit, refreshUsage }) {
  const [url, setUrl] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [phase, setPhase] = useState(0);
  const [flashing, setFlashing] = useState(false);
  const [failedUrl, setFailedUrl] = useState(null);
  const [manualLoading, setManualLoading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const fileRef = useRef(null);
  const timers = useRef([]);

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  const persistBrand = (patch) => { if (brandId) api.put(`/brands/${brandId}`, patch).catch(() => {}); };

  const analyze = async (force = false, targetUrl = null, deep = false) => {
    const u = (targetUrl || url).trim();
    if (!u) { toast("Paste a website URL first", { description: "e.g. stripe.com" }); return; }
    setFailedUrl(null);
    setAnalyzing(true);
    setFlashing(false);
    setPhase(0);
    clearTimers();
    timers.current.push(setTimeout(() => setPhase(1), 700));
    timers.current.push(setTimeout(() => setPhase(2), 4000));
    timers.current.push(setTimeout(() => setPhase(3), 9000));
    const started = Date.now();
    try {
      const { data } = await api.post("/brand/analyze", { url: u, force, deep });
      const elapsed = Date.now() - started;
      if (elapsed < 900) await sleep(900 - elapsed);
      clearTimers();
      setPhase(4);
      setFlashing(true);
      await sleep(400);
      refreshUsage?.();
      if (onAnalyzed) onAnalyzed(data); else setBrandDna(data);
    } catch (e) {
      const info = limitInfo(e);
      if (info) { onLimit?.(info); }
      else {
        setFailedUrl(u);
        toast.error(errMsg(e, "Could not analyze that site"));
      }
    } finally {
      clearTimers();
      setAnalyzing(false);
      setFlashing(false);
    }
  };

  const enterManually = async () => {
    setManualLoading(true);
    try {
      const { data } = await api.post("/brand/manual", { url: failedUrl || url.trim() });
      setFailedUrl(null);
      refreshUsage?.();
      if (onAnalyzed) onAnalyzed(data); else setBrandDna(data);
    } catch (e) {
      const info = limitInfo(e);
      if (info) onLimit?.(info);
      else toast.error(errMsg(e, "Could not create manual brand"));
    } finally {
      setManualLoading(false);
    }
  };

  const onColorsChange = (next) => { setBrandDna({ ...brandDna, colors: next }); persistBrand({ colors: next }); };
  const onVoiceChange = (v) => setBrandDna({ ...brandDna, voiceSummary: v });

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const next = [reader.result, ...(brandDna?.images || [])];
      setBrandDna({ ...brandDna, images: next });
      persistBrand({ images: next });
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className={`thin-scroll ${analyzing || pulsing ? "grid-pulse" : ""}`} style={{ position: "relative", padding: 20, height: "100%", overflowY: "auto" }} data-testid="brand-dna-panel">
      <Eyebrow testId="eyebrow-brand-dna">Brand DNA</Eyebrow>

      <div style={{ marginTop: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
          <label className="font-mono" style={{ fontSize: 10, color: "var(--tmuted)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Website URL</label>
          <InfoTip testId="tip-url" text="Paste any brand's homepage. We scan it to extract colors, fonts, imagery and brand voice. If a site can't be read, you can enter details manually." />
        </div>
        <input
          data-testid="url-input"
          className="dim-input font-body"
          placeholder="Paste any website URL..."
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && analyze(false)}
        />
        <button data-testid="analyze-button" className="btn-primary" onClick={() => analyze(false)} disabled={analyzing} style={{ width: "100%", marginTop: 12 }}>
          {analyzing ? "Scanning..." : "Analyze Brand"}
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 10 }}>
          <button data-testid="deep-analyze-button" className="btn-secondary" onClick={() => analyze(false, null, true)} disabled={analyzing} style={{ flex: 1 }}>
            <ScanSearch size={14} /> Deep Analyze
          </button>
          <InfoTip testId="tip-deep" text="Scans the homepage plus a few key inner pages (About, Products, Pricing…) for a richer palette, voice and asset set. Uses more scraping credits and takes a little longer." />
        </div>
      </div>

      <div style={{ position: "relative", marginTop: 22, minHeight: analyzing ? 360 : 0 }}>
        {analyzing && <ScanOverlay phase={phase} flashing={flashing} />}

        {brandDna && !analyzing && (
          <div data-testid="brand-dna-results">
            <SourceUrl url={brandDna.sourceUrl} />
            {(brandDna.analyzedAt || brandDna.manual) && (
              <div className="font-mono" style={{ display: "flex", alignItems: "center", gap: 10, marginTop: -8, marginBottom: 16, fontSize: 10, color: "var(--tmuted)", flexWrap: "wrap" }}>
                <span data-testid="last-analyzed">
                  {brandDna.analyzedAt
                    ? `Last analyzed: ${new Date(brandDna.analyzedAt).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}`
                    : "Manual entry"}
                </span>
                {brandDna.sourceUrl && (
                  <button
                    data-testid="reanalyze-button"
                    className="font-mono"
                    onClick={() => analyze(true, brandDna.sourceUrl)}
                    disabled={analyzing}
                    style={{ background: "transparent", border: "1px solid rgba(24, 24, 27,0.20)", color: "var(--orange)", borderRadius: 6, padding: "3px 9px", fontSize: 10, cursor: "pointer" }}
                  >
                    Re-analyze
                  </button>
                )}
                {brandId && (
                  <button
                    data-testid="share-kit-button"
                    className="font-mono"
                    onClick={() => setShareOpen(true)}
                    style={{ background: "transparent", border: "1px solid rgba(24, 24, 27,0.20)", color: brandDna.shareEnabled ? "var(--orange)" : "var(--tsecondary)", borderRadius: 6, padding: "3px 9px", fontSize: 10, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}
                  >
                    <Share2 size={11} /> {brandDna.shareEnabled ? "Shared" : "Share"}
                  </button>
                )}
              </div>
            )}
            <PaletteSection colors={brandDna.colors || []} onChange={onColorsChange} />
            <TypefacesSection fonts={brandDna.fonts || []} />
            <VoiceSection
              value={brandDna.voiceSummary || ""}
              onChange={onVoiceChange}
              onBlur={() => persistBrand({ voiceSummary: brandDna.voiceSummary })}
            />
            <AssetsSection images={brandDna.images || []} fileRef={fileRef} onUpload={onUpload} />
          </div>
        )}

        {failedUrl && !analyzing && !brandDna && (
          <div className="panel-card" data-testid="manual-fallback" style={{ marginTop: 16, padding: 16 }}>
            <div className="font-body" style={{ fontSize: 13, color: "var(--tsecondary)", lineHeight: 1.5 }}>
              We couldn't automatically analyze this site. Enter your brand details manually — add colors, edit voice and upload assets.
            </div>
            <button data-testid="enter-manually-button" className="btn-secondary" style={{ marginTop: 12 }} onClick={enterManually} disabled={manualLoading}>
              {manualLoading ? "Setting up..." : "Enter Brand Manually"}
            </button>
          </div>
        )}

        {!brandDna && !analyzing && !failedUrl && (
          <EmptyState
            testId="brand-dna-empty"
            icon={Globe}
            title="Awaiting a target URL"
            hint="Paste a website above and hit Analyze Brand. We'll extract the palette, typefaces, voice and key imagery into a living Brand DNA."
          />
        )}
      </div>

      <ShareKitDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        brandId={brandId}
        shareId={brandDna?.shareId}
        shareEnabled={brandDna?.shareEnabled}
        views={brandDna?.kitViews}
        onChange={(sid, enabled) => setBrandDna((prev) => (prev ? { ...prev, shareId: sid, shareEnabled: enabled } : prev))}
      />
    </div>
  );
}
